import { createContext, ReactNode, useContext, useMemo, useState } from 'react'
import { useQueryParamState } from '@omnea-app/fe-routing/useQueryParamState'

export const LIBRARY_PATH = '/settings/agent-library'

type LibraryNav = {
  selectedTemplateId: string | null
  clearSelection: () => void
} & (
  | {
      /** Cards are real links writing a query param, so deep links and open-in-new-tab work. */
      kind: 'url'
    }
  | {
      /** Cards are buttons: there is no page to link to, and the host page owns the URL. */
      kind: 'state'
      selectTemplate: (templateId: string) => void
    }
)

const LibraryNavContext = createContext<LibraryNav | null>(null)

export const useLibraryNav = (): LibraryNav => {
  const nav = useContext(LibraryNavContext)
  if (nav === null) throw new Error('useLibraryNav must be used inside a LibraryNavProvider')
  return nav
}

/** Selection lives in the query string, so a template can be linked to and reopened from history. */
export const useUrlLibraryNav = (): LibraryNav => {
  const [selectedTemplateId, setSelectedTemplateId] = useQueryParamState('templateId')

  return useMemo(
    () => ({ kind: 'url', selectedTemplateId, clearSelection: () => setSelectedTemplateId(null) }),
    [selectedTemplateId, setSelectedTemplateId]
  )
}

/** Selection lives in local state, for hosts whose own URL must not gain a library param. */
export const useStateLibraryNav = (): LibraryNav => {
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null)

  return useMemo(
    () => ({
      kind: 'state',
      selectedTemplateId,
      selectTemplate: setSelectedTemplateId,
      clearSelection: () => setSelectedTemplateId(null),
    }),
    [selectedTemplateId]
  )
}

type ProviderProps = {
  value: LibraryNav
  children: ReactNode
}

export const LibraryNavProvider = ({ value, children }: ProviderProps) => (
  <LibraryNavContext.Provider value={value}>{children}</LibraryNavContext.Provider>
)
